import React from "react";
import "../AntiSlaveryPolicy.css";

export default function AntiSlaveryPolicy() {
  return (
    <div className="asp-page">
      {/* HERO / BANNER */}
      <header className="asp-hero mt-20 pt-20">
        <div className="asp-hero-inner">
          <p className="asp-hero-kicker">Bluelight Healthcare Group</p>
          <h1>Anti-Slavery & Human Trafficking Policy and Procedure</h1>
          <p className="asp-hero-sub">
            Human Resources · Equality and Human Rights
          </p>
        </div>
      </header>

      <main className="asp-main">
        {/* REVIEW SHEET */}
        <section className="asp-card" id="review-sheet">
          <h2>Review Sheet</h2>

          <div className="asp-two-col">
            <div>
              <h3>Business Impact</h3>
              <p>
                Changes are important, but urgent implementation is not required and
                can be incorporated into existing workflows.
              </p>
              <p className="asp-tag asp-tag-medium">Medium Impact</p>
            </div>

            <div>
              <h3>Reason for this Review</h3>
              <p>Scheduled review</p>

              <h3>Were Changes Made?</h3>
              <p>Yes</p>
            </div>
          </div>

          <h3>Summary of Changes</h3>
          <ul>
            <li>
              A new procedure has been added in relation to the process to follow
              when there are incidences of slavery or human trafficking.
            </li>
            <li>
              Key facts have been added in relation to people affected by the
              service.
            </li>
            <li>
              Additional references have been added to the Further Reading section.
            </li>
            <li>
              An information flyer from the Salvation Army on Domestic Slavery has
              been added to Forms.
            </li>
          </ul>

          <h3>Relevant Legislation</h3>
          <ul>
            <li>The Modern Slavery Act 2015</li>
            <li>
              The Health and Social Care Act 2008 (Regulated Activities)
              (Amendment) Regulations 2015
            </li>
            <li>Human Rights Act 1998</li>
          </ul>

          <h3>Suggested Actions</h3>
          <ul>
            <li>Encourage sharing the policy through the use of the QCS App.</li>
            <li>Add the policy to planned team meeting agendas.</li>
            <li>Develop training sessions for relevant staff.</li>
            <li>
              Ensure relevant staff are aware of the content of the whole policy.
            </li>
          </ul>
        </section>

        {/* 1. PURPOSE */}
        <section className="asp-card" id="purpose">
          <h2>1. Purpose</h2>
          <p>
            1.1 To ensure that Bluelight Healthcare Group is aware of
            the issues surrounding slavery and human trafficking and has a policy
            and process in place to identify (and, if applicable, report on) issues
            surrounding slavery and human trafficking.
          </p>
          <p>
            1.2 This policy must be read alongside the Safeguarding Policy and
            Procedure of Bluelight Healthcare Group. Bluelight will
            ensure that staff understand Local Authority safeguarding reporting
            procedures where the service user is registered, and that these
            procedures are communicated to all staff.
          </p>
          <p>
            1.3 This policy supports Bluelight Healthcare Group in
            meeting the following Key Lines of Enquiry:
          </p>

          <div className="asp-table">
            <div className="asp-table-row asp-table-header">
              <div>Key Question</div>
              <div>Key Line of Enquiry (KLOE)</div>
            </div>
            <div className="asp-table-row">
              <div>SAFE</div>
              <div>
                S1: How do systems, processes and practices keep people safe and
                safeguarded from abuse?
              </div>
            </div>
            <div className="asp-table-row">
              <div>WELL-LED</div>
              <div>
                W1: Is there a clear vision and credible strategy to deliver
                high-quality care and support, and promote a positive culture that
                is person-centred, open, inclusive and empowering, which achieves
                good outcomes for people?
              </div>
            </div>
            <div className="asp-table-row">
              <div>WELL-LED</div>
              <div>
                W2: Does the governance framework ensure that responsibilities are
                clear and that quality performance, risks and regulatory
                requirements are understood and managed?
              </div>
            </div>
          </div>

          <p>
            1.4 To meet the legal requirements of the regulated activities that
            Bluelight Healthcare Group is registered to provide,
            including:
          </p>
          <ul>
            <li>The Modern Slavery Act 2015</li>
            <li>
              The Health and Social Care Act 2008 (Regulated Activities)
              (Amendment) Regulations 2015
            </li>
            <li>Human Rights Act 1998</li>
          </ul>
        </section>

        {/* 2. SCOPE */}
        <section className="asp-card" id="scope">
          <h2>2. Scope</h2>

          <h3>2.1 Roles Affected by this Policy</h3>
          <ul>
            <li>All staff</li>
            <li>Registered Manager</li>
            <li>Owner, Proprietor or Responsible Individual</li>
          </ul>

          <h3>2.2 Service Users Affected</h3>
          <ul>
            <li>Service Users</li>
          </ul>

          <h3>2.3 Stakeholders Affected</h3>
          <ul>
            <li>Commissioners</li>
            <li>Local Authority</li>
          </ul>
        </section>

        {/* 3. OBJECTIVES */}
        <section className="asp-card" id="objectives">
          <h2>3. Objectives</h2>
          <p>
            3.1 To promote awareness of concerns surrounding slavery and human
            trafficking, and to promote the commitment of Bluelight Healthcare
            Group in addressing slavery and human trafficking in all
            its forms. An annual statement will be produced, where applicable.
          </p>
        </section>

        {/* 4. POLICY */}
        <section className="asp-card" id="policy">
          <h2>4. Policy</h2>

          <p>
            4.1 It is unlikely that Bluelight Healthcare Group will
            experience cases of slavery or human trafficking, but this policy
            details the organisation’s commitment to raise awareness and defines a
            process to address issues if they do arise. It acknowledges that human
            trafficking and modern slavery are safeguarding matters.
          </p>

          <p>
            4.2 Slavery and human trafficking is classed as abuse. Indicators
            could include:
          </p>
          <ul>
            <li>Signs of physical or emotional abuse</li>
            <li>Appearing malnourished, unkempt or withdrawn</li>
            <li>
              Isolation from the community; seeming under the control or influence
              of others
            </li>
            <li>
              Living in dirty, cramped or overcrowded accommodation, or living and
              working at the same address
            </li>
            <li>Lack of personal effects or identification documents</li>
            <li>Always wearing the same clothes</li>
            <li>
              Avoidance of eye contact; appearing frightened or hesitant to talk
              to strangers
            </li>
            <li>Fear of law enforcers</li>
          </ul>

          <p>
            4.3 All staff will be made aware of the issues surrounding slavery and
            human trafficking, and will be encouraged and supported to report any
            concerns to Bluelight management. Bluelight will support any staff who
            may be subject to slavery or human trafficking.
          </p>

          <p>
            4.4 Bluelight will ensure that only staff whose credentials can be
            confirmed are employed.
          </p>

          <p>
            4.5 In situations where slavery or human trafficking is identified,
            Bluelight will share this information with the Local Authority
            Safeguarding Team where the service user is registered, to safeguard
            individuals from harm and help prevent future situations from arising.
          </p>

          <p>
            4.6 Section 54 of the Modern Slavery Act 2015 states that every
            organisation providing goods and services in the UK with a total global
            annual turnover of £36 million or more must produce a slavery and
            human trafficking statement for each financial year. Organisations
            below this threshold may produce a statement voluntarily.
          </p>

          <p>
            4.7 The £36m threshold applies to the total turnover of the organisation
            or its group, not the individual location. Publication of the annual
            statement (if applicable) is primarily the responsibility of senior
            management, but individual services will contribute to the content and
            maintain awareness of slavery and human trafficking issues.
          </p>

          <p>
            4.8 Statements should normally be published within six months of the
            financial year end.
          </p>
        </section>

        {/* 5. PROCEDURE (SUMMARY) */}
        <section className="asp-card" id="procedure">
          <h2>5. Procedure</h2>

          <h3>5.1 The Annual Statement (where applicable)</h3>
          <p>
            If Bluelight Healthcare Group has a turnover of more
            than £36m, the annual statement will include details of:
          </p>
          <ul>
            <li>Structure, business and supply chains</li>
            <li>Policies in relation to slavery and human trafficking</li>
            <li>
              Due diligence processes in relation to slavery and human trafficking
              in business and supply chains
            </li>
            <li>
              Risk areas and steps taken to assess and manage those risks
            </li>
            <li>
              Effectiveness in ensuring slavery and human trafficking is not taking
              place, measured with appropriate performance indicators
            </li>
            <li>Training available to staff on slavery and human trafficking</li>
          </ul>

          <p>
            5.2 The statement will be written in simple language, be succinct yet
            cover all relevant points, and *must* be true and built upon year after
            year.
          </p>

          <p>
            5.5 The statement will be published on the Bluelight website and, where
            appropriate, linked through to any other relevant websites of Bluelight
            Healthcare Group.
          </p>

          <h3>5.9 Staff Requirements</h3>
          <p>
            All staff engaged in providing services will be subject to thorough
            recruitment procedures including DBS check, identity check, right to
            work in the UK, employment history, suitability for the role and
            reference checks.
          </p>

          <p>
            5.10 Only staff provided by appropriately registered third-party
            organisations, who can confirm staff are free to work and meet all
            requirements, will be used.
          </p>

          <p>5.11 All staff will receive information about slavery and human trafficking.</p>

          <p>
            5.12 Staff will be advised that if they are subject to slavery or human
            trafficking, are aware of any individual who may be subject to slavery
            or human trafficking, or if slavery/human trafficking is disclosed to
            them, they must inform the Registered Manager or the police.
          </p>

          <h3>5.13 Incidences of Slavery and Human Trafficking</h3>
          <p>
            The Responsible Individual, <strong>Linda Wacha Akedo</strong>, is the
            main point of contact within Bluelight Healthcare Group
            for reporting cases and advising on actions to be taken.
          </p>
          <p>
            Specific cases must be reported to the Registered Manager and Linda
            Wacha Akedo, as well as the police in the first instance. Where
            applicable, the Local Authority must be informed and a notification
            submitted to the Care Quality Commission.
          </p>

          <h3>5.14 Review of Effectiveness</h3>
          <p>
            Bluelight intends to take further steps to identify, assess and monitor
            potential risk areas, particularly in supply chains, and will:
          </p>
          <ul>
            <li>
              Support staff to understand and respond to modern slavery and human
              trafficking.
            </li>
            <li>
              Gain assurance that all staff have access to training on how to
              identify victims.
            </li>
            <li>
              Review Safeguarding Policy and Procedure so that Modern Slavery and
              Human Trafficking remain integral to its content.
            </li>
          </ul>
        </section>

        {/* 6. DEFINITIONS */}
        <section className="asp-card" id="definitions">
          <h2>6. Definitions</h2>

          <h3>6.1 Slavery</h3>
          <p>
            In accordance with the 1926 Slavery Convention, slavery is the status
            or condition of a person over whom all or any of the powers attaching
            to the right of ownership are exercised. Although legal ownership is
            not possible, the key element is behaviour as if the offender owns the
            person, depriving the victim of their freedom.
          </p>

          <h3>6.2 Human Trafficking</h3>
          <p>
            An offence requires that a person arranges or facilitates the travel of
            another person with a view to that person being exploited. The offence
            can be committed even where the victim consents to the travel. The
            exploitation does not need to have taken place; it is enough that the
            travel was arranged with a view to exploitation (sexual or non-sexual).
          </p>

          <h3>6.3 Turnover</h3>
          <p>
            “Turnover” means the amount derived from the provision of goods and
            services falling within the ordinary activities of the commercial
            organisation or subsidiary, after deduction of trade discounts, VAT and
            other relevant taxes.
          </p>
        </section>

        {/* KEY FACTS & FURTHER READING */}
        <section className="asp-card asp-card-highlight" id="key-facts">
          <h2>Key Facts – Professionals</h2>
          <ul>
            <li>
              The Modern Slavery Act 2015 details what organisations need to do
              about slavery and human trafficking.
            </li>
            <li>
              Staff will receive information about slavery and human trafficking,
              and will be supported by Bluelight if subject to, or reporting, any
              cases.
            </li>
            <li>
              Only staff who have been through robust recruitment procedures will
              be employed.
            </li>
            <li>
              Bluelight will share information with appropriate authorities where
              there are cases of slavery or human trafficking.
            </li>
            <li>
              Disclosures must be shared with the Registered Manager or the police.
            </li>
            <li>
              Where applicable, an annual statement on modern slavery and human
              trafficking will be issued.
            </li>
          </ul>

          <h2>Key Facts – People Affected by the Service</h2>
          <ul>
            <li>
              Any awareness of, or involvement in, acts of modern slavery or human
              trafficking can be reported to Bluelight, and support will be
              provided.
            </li>
            <li>
              Care is provided by staff who have been through robust recruitment
              procedures.
            </li>
          </ul>

          <h2>Further Reading</h2>
          <p>
            In addition to the underpinning knowledge, staff are encouraged to
            review the resources listed in the policy (e.g. Home Office guidance,
            SCIE materials, Ethical Trading Initiative resources, and modern
            slavery reporting guides).
          </p>
        </section>

        {/* OUTSTANDING PRACTICE & FORMS */}
        <section className="asp-card" id="outstanding-practice">
          <h2>Outstanding Practice</h2>
          <p>
            To be outstanding in this policy area, Bluelight may provide evidence
            that:
          </p>
          <ul>
            <li>Specific training is provided on slavery and human trafficking.</li>
            <li>
              A statement on slavery and human trafficking is published even if
              not legally required.
            </li>
            <li>
              There is a very high level of understanding and awareness in all
              areas of the service.
            </li>
            <li>
              Bluelight shares its practice with other organisations and is seen
              as a best-practice resource.
            </li>
            <li>
              The supply chain is regularly audited with thorough due diligence.
            </li>
            <li>
              Understanding of this policy is enabled by proactive use of tools
              such as the QCS App.
            </li>
          </ul>

          <h2>Forms</h2>
          <ul>
            <li>
              <strong>Domestic Slavery</strong> – used when undertaking
              safeguarding training (created by The Salvation Army).
            </li>
          </ul>
        </section>

        {/* OPTIONAL: HELPLINE BLOCK */}
        <section className="asp-card asp-helpline" id="helpline">
          <h2>Modern Slavery Helpline</h2>
          <p>
            To report concerns, seek advice or get help, call the confidential UK
            Modern Slavery Helpline:
          </p>
          <p className="asp-helpline-number">0800&nbsp;012&nbsp;1700</p>
          <p>
            or visit{" "}
            <a
              href="https://www.modernslaveryhelpline.org"
              target="_blank"
              rel="noreferrer"
            >
              modernslaveryhelpline.org
            </a>
          </p>
        </section>
      </main>
    </div>
  );
}
